import React, { useState, useEffect } from 'react';

import TuneIcon from '@mui/icons-material/Tune';
import { Box, Button, Drawer } from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro';

import GridToolbarCustomExport from 'src/utils/folders/GridToolbarCustomExport';

import useLocales from '../../hooks/useLocales';
import { SavedFiltersProps } from './ViewSettings';
import useResponsive from '../../hooks/useResponsive';
import GridToolbarCleanAll from './GridToolbarCleanAll';
import useNotifications from '../../hooks/useNotifications';
import GridToolbarFormSelector from './GridToolbarFormSelector';
import viewSettingsService from '../../services/viewSettingsService';
import GridToolbarSaveFilterButton from './GridToolbarSaveFilterButton';
import GridToolbarSavedFilterSelector from './GridToolbarSavedFilterSelector';
import { DocumentModuleTypes } from '../../@types/shared/enums/documentModuleTypes';

// ----------------------------------------------------------------------

const DocumentGridToolbar: React.FC = () => {
  const { t } = useLocales();
  const { enqueueError } = useNotifications();
  const isMobile = useResponsive('down', 'sm');
  const [savedFilters, setSavedFilters] = useState<SavedFiltersProps[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const getSavedFilters = async () => {
    try {
      const data: SavedFiltersProps[] = await viewSettingsService.getViews(
        DocumentModuleTypes.Standard
      );

      setSavedFilters(data);
    } catch (e) {
      enqueueError(e);
    }
  };

  useEffect(() => {
    getSavedFilters().then();
  }, []);

  const toolbarButtons = (
    <>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarSavedFilterSelector
        savedFilters={savedFilters}
        getSavedFilters={getSavedFilters}
      />
      <GridToolbarSaveFilterButton getSavedFilters={getSavedFilters} />
      <GridToolbarFormSelector />
      <GridToolbarDensitySelector />
      <GridToolbarCustomExport />
      <GridToolbarCleanAll />
    </>
  );

  if (isMobile) {
    return (
      <GridToolbarContainer
        style={{
          paddingTop: '6px',
          paddingLeft: 0,
          paddingRight: 0,
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <GridToolbarQuickFilter sx={{ flex: 1 }} />
          <Button
            size="small"
            startIcon={<TuneIcon />}
            onClick={() => setDrawerOpen(true)}
            sx={{ minWidth: 'fit-content', height: 40 }}
          >
            {t('shared.buttons.filterBy')}
          </Button>
        </Box>

        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              px: 2,
              py: 3,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              '& .MuiButton-root': {
                justifyContent: 'flex-start',
              },
            }}
          >
            {toolbarButtons}
          </Box>
        </Drawer>
      </GridToolbarContainer>
    );
  }

  return (
    <GridToolbarContainer
      style={{
        paddingTop: '6px',
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center', flexWrap: 'wrap' }}
        >
          {toolbarButtons}
        </Box>

        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
};

export default DocumentGridToolbar;
